import Page from '../../Page';
import { useState } from 'react';
import React, { useEffect } from "react";
import { Container, createTheme, ThemeProvider, Skeleton } from '@mui/material';
import MUIDataTable, { ExpandButton } from "mui-datatables";
import UserInfo from 'src/utils/Authorization/UserInfo';
import axios from 'src/utils/apiAuth';
import * as constants from 'src/utils/constants';
import { debounce } from 'src/utils/debounceSearch';
import { getDDMMYYYYFormat} from 'src/utils/commonUtils';
import CalendarRangePicker from 'src/components/CalendarRangePicker';
import {CheckCircle, Cancel} from '@mui/icons-material';

const theme = createTheme({
    components: {
        MUIDataTable: {
            styleOverrides: {
                root: {
                    backgroundColor: '#red',
                },
                paper: {
                    boxShadow: 'none',
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    // backgroundColor: 'yellow',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    backgroundColor: 'purple',
                },
            },
        },
        MUIDataTableHeadCell: {
            styleOverrides: {
                root: {
                    fontWeight: '700',
                },
                data: {
                    fontWeight: '700',
                }
            }
        },
        MUIDataTableSelectCell: {
            styleOverrides: {
                headerCell: {
                    // backgroundColor: 'blue',
                    fontWeight: '500px'
                },
            },
        },
        MuiTableFooter: {
            styleOverrides: {
                root: {
                    '& .MuiToolbar-root': {
                        //   backgroundColor: 'purple',
                    },
                },
            },
        },
    },
    typography: {
        fontFamily: "Lexend",
    }
});

const components = {
    ExpandButton: function (props) {
        return <ExpandButton {...props} />;
    }
};

export default function EmailLogs() {
    // state to save job application ID
    const userInfo = UserInfo();
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [count, setCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10); // default rows per page
    const [searchQuery, setSearchQuery] = useState('');
    const [filterParams, setFilterParams] = useState({});
    const [sortOrder, setSortOrder] = useState();
    const [selectedDateRange, setSelectedDateRange] = useState([]);

    const debouncedSearch = debounce((query) => {
        setSearchQuery(query);
    }, 1000); 

    // Function to append filters to the URL
    const applyFiltersToUrl = (filterParams) => {
        let url = "";
        for (const key in filterParams) {
            if (filterParams[key] && Array.isArray(filterParams[key]) && filterParams[key].length > 0) {
                url += `${key}=${filterParams[key].join(",")}&`;
            }
        }    
        return url;
    };

    const [emailLogsData, setEmailLogsData] = useState([])
    const getEmailLogs = (page, pageSize = 50, query = '', selectedDateRange = [], filterParams = {}, sortOrder = null) => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/email-logs?`;
        if (query) apiUrl += `search=${query}&`;
        if (page + 1 && pageSize) apiUrl += `page=${page + 1}&page_size=${pageSize}&`;
        if (sortOrder && sortOrder?.name && sortOrder?.direction) apiUrl += `sortfield=${sortOrder.name}&sortdirection=${sortOrder.direction}&`
        if (selectedDateRange.length > 0) apiUrl += `${selectedDateRange[0] ? `start_date=${selectedDateRange[0]}&` : ''}${selectedDateRange[1] ? `end_date=${selectedDateRange[1]}&` : ''}${selectedDateRange[2] ? `date_filter_type=${selectedDateRange[2]}&` : ''}`;

        // Append filters to the URL
        apiUrl += applyFiltersToUrl(filterParams);

        // remove the trailing '&' from the URL
        if (apiUrl.endsWith("&")) apiUrl = apiUrl.slice(0, -1);

        axios.get(apiUrl, { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } })
            .then((response) => {
                setEmailLogsData(response?.data?.results?.data);
                setCount(response?.data?.count);
                setIsLoading(false);
            })
            .catch((e) => console.log('something went wrong :(', e));
    }

    const options = {
        selectableRows: "none",
        filter: true,
        print: false,
        filterType: "multiselect",
        responsive: "standard",
        draggableColumns: { enabled: true },
        download: false,
        serverSide: true,
        count: count,
        rowsPerPage: rowsPerPage,
        page: currentPage,
        onTableChange: (action, tableState) => {
            switch (action) {
                case "changePage":
                    setCurrentPage(tableState.page);
                    break;
                case "changeRowsPerPage":
                    setRowsPerPage(tableState.rowsPerPage);
                    break;
                case "search":
                    debouncedSearch(tableState.searchText);
                    setCurrentPage(0);
                    break;
                case 'sort':
                    setSortOrder(tableState.sortOrder);
                    break;
                default:
                    break;
            }
        },
        onFilterChange: (column, filterList, type) => {
            const index = columns.findIndex((item) => item.name === column);
            const filterValue = filterList[index];
            setCurrentPage(0);

            let newFilterParams = { ...filterParams };

            switch (column) {
                case "delivered":
                    newFilterParams.delivered = filterValue;
                    break;
                case null:
                    newFilterParams = {};
                    break;
                default:
                    break;
            }
            // Remove the property if it has no value
            if (newFilterParams[column] && newFilterParams[column].length === 0) {
                delete newFilterParams[column];
            }
            setFilterParams(newFilterParams);
        },
    };

    const columns = [
        {
            name: "id",
            label: "ID",
            options: {
                filter: false,
                display: false,
            },
        },
        {
            name: "subject",
            label: "Subject",
            options: {
                filter: false,
            },
        },
        {
            name: "receiver_emails",
            label: "Receiver Emails",
            options: {
                filter: false,
            },
        },
        {
            name: "cc_emails",
            label: "CC Emails",
            options: {
                filter: false,
            },
        },
        {
            name: "body_type",
            label: "Body Type",
            options: {
                filter: false,
                display: false
            },
        },
        {
            name: "message",
            label: "Message",
            options: {
                filter: false,
                display: false,
            },
        },
        {
            name: "ics_data",
            label: "ICS File Data",
            options: {
                filter: false,
                display: false,
            },
        },
        {
            name: "sender",
            label: "Sender",
            options: {
                filter: false,
            },
        },
        {
            name: "created",
            label: "Created",
            options: {
                filter: false,
                customBodyRender: (value) => {
                    return (
                        <p>{getDDMMYYYYFormat(value)}</p>
                    )
                }
            }
        },
        {
            name: "delivered",
            label: "Delivered?",
            options: {
                filter: true,
                filterOptions: {
                    fullWidth: true,
                    names: ['Yes', 'No'],
                    logic(delivered, filterVal) {
                        if (
                            (filterVal.includes('Yes') && delivered) ||
                            (filterVal.includes('No') && !delivered)
                        ) {
                            return false; // Matches the filter, don't filter out
                        }
                        return true; // Filter out everything else
                    }
                },
                customBodyRender: (value) => (
                    value ? <CheckCircle color='success'/> : <Cancel color='error'/>
                )
            }
        }
    ];

    useEffect(() => {
        getEmailLogs(currentPage, rowsPerPage, searchQuery, selectedDateRange, filterParams, sortOrder)
    }, [currentPage, rowsPerPage, searchQuery, selectedDateRange, filterParams, sortOrder])

    return (
        <Page title="Email Logs | CRM">
            <Container maxWidth="xl">
                <h2 align="center" style={{ fontSize: "25px", fontWeight: "800", marginBottom: "30px" }}>
                    Email Logs
                </h2>

                {isLoading ? (
                    <div style={{ padding: "16px", marginTop: "20px" }}>
                        {/* Skeleton for the Table Header */}
                        <div style={{ display: "flex", marginBottom: "20px" }}>
                            {columns.map((column, colIndex) => (
                                <Skeleton
                                    key={colIndex}
                                    variant="text"
                                    width={colIndex === 2 ? "20%" : "10%"}
                                    style={{ marginRight: "16px" }}
                                    height={50}
                                />
                            ))}
                        </div>

                        {/* Skeletons for the Table Rows */}
                        {Array.from(new Array(rowsPerPage)).map((_, rowIndex) => (
                            <div key={rowIndex} style={{ display: "flex", marginBottom: "10px" }}>
                                {columns.map((column, colIndex) => (
                                    <Skeleton
                                        key={colIndex}
                                        variant="text"
                                        width={colIndex === 2 ? "20%" : "10%"} // Assuming Candidate Name column might be wider
                                        style={{ marginRight: "16px" }}
                                        height={50}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <ThemeProvider theme={theme}>
                        <MUIDataTable
                            title={
                                <>
                                    <div></div>
                                    <CalendarRangePicker onChange={(value)=>{setSelectedDateRange(value)}}/>
                                </>
                            }
                            data={emailLogsData?.slice(0).map(data => ({
                                id: data?.id,
                                subject: data?.subject,
                                receiver_emails: data?.receiver_emails?.map((mail) => mail).join(", "),
                                cc_emails: data?.cc_emails?.map((mail) => mail).join(", "),
                                body_type: data?.body_type,
                                message: data?.message,
                                ics_data: data?.ics_data,
                                sender: data?.sender ? data?.sender?.first_name + " " + data?.sender?.last_name : null,
                                created: data?.created,
                                delivered: data?.delivered,
                            }))}
                            columns={columns}
                            options={options}
                            components={components}
                        />
                    </ThemeProvider>
                )}
            </Container>
        </Page>
    );
}